import { useMutation, useQuery } from '@apollo/client/react';
import { useCallback, useMemo, useState } from 'react';
import { useErrorHandler } from 'react-error-boundary';

import { AppError } from '~/error';
import { ChatListDocument, InvokeMemberActionDocument, MemberActionType, GetMemberActionsDocument } from '~/types';

export const useMemberActionMutation = () => {
    const errorHandler = useErrorHandler();
    const [memberActionMutation] = useMutation(InvokeMemberActionDocument);
    const [loading, setLoading] = useState(false);

    const { data } = useQuery(GetMemberActionsDocument);
    const chatActionTypes = useMemo(
        () => data?.root?.availableActions?.chatActionTypes ?? [],
        [data?.root?.availableActions?.chatActionTypes]
    );

    const onAction = useCallback(
        async (action: MemberActionType, title?: string, reasonForContact?: string) => {
            if (chatActionTypes.includes(action)) {
                setLoading(true);

                try {
                    const result = await memberActionMutation({
                        variables: {
                            input: {
                                typeNew: action,
                                title,
                                reasonForContact
                            }
                        },
                        refetchQueries: [{ query: ChatListDocument }]
                    });

                    return result.data?.memberAction?.appUrl;
                } catch (err: unknown) {
                    if (err instanceof Error) {
                        errorHandler(
                            new AppError(err, 'error.cannot-create-chat', {
                                onClose: () => {},
                                name: 'error-overlay.go_back'
                            })
                        );
                    } else {
                        errorHandler(err);
                    }
                }
                setLoading(false);
            }
        },
        [errorHandler, memberActionMutation, chatActionTypes]
    );

    return {
        loading,
        onAction
    };
};
